import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import { CompanyDetailsModule } from '@/store/modules/companyDetails';

import NewBookingOptionsDto from '@/models/Bookings/NewBookingOptionsDto';
import NewBookingViewModel from '@/models/Bookings/NewBookingViewModel';
import LocationDto from '@/models/Locations/LocationDto';
import PositionDto from '@/models/Positions/PositionDto';
import SkillDto from '@/models/Skills/SkillDto';
import QualificationDto from '@/models/Qualifications/QualificationDto';
import UniformDto from '@/models/Uniforms/UniformDto';
import BookingRoleViewModel from '@/models/BookingRoles/BookingRoleViewModel';
import CreateBookingRequest from '@/models/Bookings/CreateBookingRequest';
import BookingDto from '@/models/Bookings/BookingDto';
import CreateBookingRoleRequest from '@/models/BookingRoles/CreateBookingRoleRequest';
import ShiftDto from '@/models/Shifts/ShiftDto';
import DepartmentDto from '@/models/Departments/DepartmentDto';
import BranchDto from '@/models/Branches/BranchDto';
import BookingRoleDto from '@/models/BookingRoles/BookingRoleDto';
import DeskDto from '@/models/Desks/DeskDto';
import ContactDto from '@/models/Contacts/ContactDto';
import AgencyUserDto from '@/models/AgencyUsers/AgencyUserDto';
import ContactLocationDto from '@/models/ContactLocations/ContactLocationDto';
import CreateShiftRequest from '@/models/Shifts/CreateShiftRequest';
import RateCardPositionDto from '@/models/RateCardPositions/RateCardPositionDto';

export interface IBookingWizardState {
  loading: boolean;
  isError: boolean;
  newBookingOptions: NewBookingOptionsDto;
  newBooking: NewBookingViewModel;
  newBookingRole: BookingRoleViewModel;
  loadingDialog: boolean;
  
}

@Module({ dynamic: true, store, name: 'BookingWizard', namespaced: true})
class BookingWizard extends VuexModule implements IBookingWizardState {
  
  public loading = false; 
  public isError = false;
  public newBookingOptions: NewBookingOptionsDto = new NewBookingOptionsDto();
  public newBooking: NewBookingViewModel = new NewBookingViewModel();
  public newBookingRole: BookingRoleViewModel = new BookingRoleViewModel();
  public loadingDialog = false;

  @Action
  public async getNewBookingOptions(companyId: string) {
    this.setLoading(true); 
    await ajax.get(`/api/v1/agency/companies/${companyId}/new-booking-options`)
    .then(response => {
      this.context.commit("setNewBookingOptions", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    })
  }

  @Action
  public async createNewBooking() {

    //Convert the NewBookingViewModel to a CreateBookingRequest
    const request = new CreateBookingRequest();
    request.branchId = this.newBooking.branch.id;
    request.locationId = this.newBooking.location.id;
    request.departmentId = this.newBooking.department.id;
    request.ownerId = this.newBooking.owner.id;
    request.isTest = this.newBooking.isTest;
    if (request.isTest) {
      request.testEmailAddresses = this.newBooking.testEmailAddresses;
    }
    
    //Add the dates
    for (let index = 0; index < this.newBooking.dates.length; index++) {
      const dateString = this.newBooking.dates[index];
      request.dates.push(new Date(`${dateString}T12:00`));
    }

    //Add the roles
    for (let index = 0; index < this.newBooking.roles.length; index++) {
      const roleVM = this.newBooking.roles[index];
      const roleRequest = new CreateBookingRoleRequest();
      roleRequest.ownerId = roleVM.owner.id;
      roleRequest.contactId = roleVM.contactLocation.contactId;
      roleRequest.positionId = roleVM.rateCardPosition.positionId;
      roleRequest.positionDescription = roleVM.positionDescription;
	  roleRequest.chargeRate = roleVM.chargeRate;
      roleRequest.deskId = roleVM.desk.id;
      roleRequest.uniformId = roleVM.uniform.id;
      roleRequest.uniformDescription = roleVM.uniformDescription
      roleRequest.skillIds = roleVM.skills.map(function(skill) {
        return skill['id'];
      });
      roleRequest.qualificationIds = roleVM.qualifications.map(function(qualification) {
        return qualification['id'];
      });

      //Add the shifts
      for (let index = 0; index < roleVM.shifts.length; index++) {
        const shiftVM = roleVM.shifts[index];
        const shiftRequest = new CreateShiftRequest();
        //shiftDto.agencyId = AppModule.currentAgencyContext.id;
        //console.log(`Start At: ${shiftVM.startAtDate}T${shiftVM.endAtTime}`)
        shiftRequest.startAt = new Date(`${shiftVM.startAtDate}T${shiftVM.startAtTime}`)
        const endAt = new Date(`${shiftVM.startAtDate}T${shiftVM.endAtTime}`)
        //console.log(`End At: ${shiftVM.startAtDate}T${shiftVM.endAtTime}`)
        if (shiftRequest.startAt > endAt){
          //console.log(`Start at greater than end at`)
          endAt.setDate(endAt.getDate() +1)
        }
        shiftRequest.endAt = endAt
        //console.log(shiftVM.quantity)
        shiftRequest.quantity = shiftVM.quantity as number;
        shiftRequest.break = shiftVM.break;
        shiftRequest.isPaidBreak = shiftVM.isPaidBreak;
        roleRequest.shifts.push(shiftRequest);
      }
      request.roles.push(roleRequest);

    }
    this.setLoading(true); 
	this.setIsError(false);
    await ajax.post(`/api/v1/agency/companies/${BookingWizardModule.newBookingOptions.company.id}/bookings`, request)
    .then(response => {
      this.context.commit("setCreateBooking", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
	  this.setIsError(true);
	  this.setLoading(false);
    })
  }

  @Action
  public async updateNewBookingBranch(branch: BranchDto){
    this.context.commit("setNewBookingBranch", branch);
  }

  @Action
  public async updateNewBookingLocation(location: LocationDto){
	//Fetch the location by ID to get the rate card
	await ajax.get(`/api/v1/agency/locations/${location.id}/booking-options`)
	  .then(response => {
		this.context.commit("setNewBookingLocation", response.data );
	  })
	  .catch(error => {
		AppModule.handleApiError(error);
	  });
  }
  
  @Action
  public async updateNewBookingDepartment(department: DepartmentDto){
    this.context.commit("setNewBookingDepartment", department);
  }

  @Action
  public async updateNewBookingIsTest(isTest: boolean){
    this.context.commit("setNewBookingIsTest", isTest);
  }

  @Action
  public async updateNewBookingTestEmailAddresses(testEmailAddresses: string){
    this.context.commit("setNewBookingTestEmailAddresses", testEmailAddresses);
  }

  @Action
  public async updateNewBookingOwner(agencyUser: AgencyUserDto){
    this.context.commit("setNewBookingOwner", agencyUser);
  }

  @Action
  public async updateNewBookingDates(dates: string[]){
    this.context.commit("setNewBookingDates", dates);
  }

  @Action
  public async updateNewBookingRolePosition(rateCardPosition: RateCardPositionDto){
    this.context.commit("setNewBookingRolePosition", rateCardPosition);
  }

  @Action
  public async updateNewBookingRoleOwner(owner: AgencyUserDto){
    this.context.commit("setNewBookingRoleOwner", owner);
  }

  @Action
  public async updateNewBookingRoleContactLocation(contactLocation: ContactLocationDto){
    this.context.commit("setNewBookingRoleContactLocation", contactLocation);
  }

  @Action
  public async updateNewBookingRolePositionDescription(description: string){
    this.context.commit("setNewBookingRolePositionDescription", description);
  }

  @Action
  public async updateNewBookingRoleSkills(skills: SkillDto[]){
    this.context.commit("setNewBookingRoleSkills", skills);
  }

  @Action
  public async updateNewBookingRoleQualifications(qualifications: QualificationDto[]){
    this.context.commit("setNewBookingRoleQualifications", qualifications);
  }

  @Action
  public async updateNewBookingRoleUniform(uniform: UniformDto){
    this.context.commit("setNewBookingRoleUniform", uniform);
  }

  @Action
  public async updateNewBookingRoleChargeRate(chargeRate: number){
    this.context.commit("setNewBookingRoleChargeRate", chargeRate);
  }

  @Action
  public async updateNewBookingRoleDesk(desk: DeskDto){
    this.context.commit("setNewBookingRoleDesk", desk);
  }

  @Action
  public async updateNewBookingRoleUniformDescription(description: string){
    this.context.commit("setNewBookingRoleUniformDescription", description);
  }

  @Action
  public async updateNewBookingRoleQuantity(quantity: number){
    this.context.commit("setNewBookingRoleQuantity", quantity);
  }

  @Action
  public async updateNewBookingRoleStartAtTime(startAtTime: string){
    this.context.commit("setNewBookingRoleStartAtTime", startAtTime);
  }

  @Action
  public async updateNewBookingRoleEndAtTime(endAtTime: string){
    this.context.commit("setNewBookingRoleEndAtTime", endAtTime);
  }

  @Action
  public async cleanBookingRoleState() {
    this.context.commit("resetBookingRoleState");
  }

  @Action
  public async addNewBookingRole(bookingRole: BookingRoleViewModel) {
    this.context.commit("setNewBookingRole", bookingRole);
  }

  @Action
  public async updateNewBookingRole(bookingRole: BookingRoleViewModel) {
    this.context.commit("setUpdateNewBookingRole", bookingRole);
  }

  @Action
  public async removeNewBookingRole(bookingRole: BookingRoleViewModel) {
    this.context.commit("deleteNewBookingRole", bookingRole);
  }

  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.loading = false;
    this.loadingDialog = false;
    this.newBookingOptions = new NewBookingOptionsDto();
    this.newBooking = new NewBookingViewModel();
    this.newBookingRole = new BookingRoleViewModel();
  }

  @Mutation
  resetBookingRoleState() {
    this.newBookingRole = new BookingRoleViewModel();
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setIsError(isError: boolean) {
      this.isError = isError;
  }

  @Mutation
  private async setLoadingDialog(isLoadingDialog: boolean) {
      this.loadingDialog = isLoadingDialog;
  }
  
  @Mutation
  private async setNewBookingOptions(newBookingOptions: NewBookingOptionsDto) {
    this.newBookingOptions = newBookingOptions;
    //also clear the booking - we just created a new one
    this.newBooking = new NewBookingViewModel();
    this.newBooking.owner = newBookingOptions.currentAgencyUser;
    this.newBookingRole = new BookingRoleViewModel();
  }

  @Mutation
  private async setNewBookingBranch(branch: BranchDto) {
      this.newBooking.branch = branch;
  }

  @Mutation
  private async setNewBookingLocation(location: LocationDto) {
      this.newBooking.location = location;
  }

  @Mutation
  private async setNewBookingDepartment(department: DepartmentDto) {
      this.newBooking.department = department;
  }

  @Mutation
  private async setNewBookingIsTest(isTest: boolean) {
      this.newBooking.isTest = isTest;
  }

  @Mutation
  private async setNewBookingTestEmailAddresses(testEmailAddresses: string) {
      this.newBooking.testEmailAddresses = testEmailAddresses;
  }

  @Mutation
  private async setNewBookingOwner(agencyUser: AgencyUserDto) {
      this.newBooking.owner = agencyUser;
  }

  @Mutation
  private async setNewBookingDates(dates: string[]) {
      this.newBooking.dates = dates;
  }

  @Mutation
  private async setNewBookingRolePosition(rateCardPosition: RateCardPositionDto) {
      this.newBookingRole.rateCardPosition = rateCardPosition;
      this.newBookingRole.positionDescription = rateCardPosition.position.description;
	  this.newBookingRole.chargeRate = rateCardPosition.chargeRate;
    //TODO:DESK
      //this.newBookingRole.desk = rateCardPosition.position.desk;
      this.newBookingRole.skills = [];
      this.newBookingRole.qualifications = [];
  }

  @Mutation
  private async setNewBookingRoleOwner(owner: AgencyUserDto) {
      this.newBookingRole.owner = owner
  }

  @Mutation
  private async setNewBookingRoleContactLocation(contactLocation: ContactLocationDto) {
      this.newBookingRole.contactLocation = contactLocation
  }

  @Mutation
  private async setNewBookingRoleDesk(desk: DeskDto) {
      this.newBookingRole.desk = desk;
  }

  @Mutation
  private async setNewBookingRolePositionDescription(description: string) {
      this.newBookingRole.positionDescription = description;
  }

  @Mutation
  private async setNewBookingRoleSkills(skills: SkillDto[]) {
      this.newBookingRole.skills = skills;
  }

  @Mutation
  private async setNewBookingRoleQualifications(qualifications: QualificationDto[]) {
      this.newBookingRole.qualifications = qualifications;
  }

  @Mutation
  private async setNewBookingRoleUniform(uniform: UniformDto) {
      this.newBookingRole.uniform = uniform;
      this.newBookingRole.uniformDescription = uniform.description;
  }

  @Mutation
  private async setNewBookingRoleChargeRate(chargeRate: number) {
      this.newBookingRole.chargeRate = chargeRate;
  }

  @Mutation
  private async setNewBookingRoleUniformDescription(description: string) {
      this.newBookingRole.uniformDescription = description;
  }

  @Mutation
  private async setNewBookingRoleQuantity(quantity: number) {
      this.newBookingRole.quantity = quantity;
  }

  @Mutation
  private async setNewBookingRoleStartAtTime(startAtTime: string) {
      this.newBookingRole.startAtTime = startAtTime;
  }

  @Mutation
  private async setNewBookingRoleEndAtTime(endAtTime: string) {
      this.newBookingRole.endAtTime = endAtTime;
  }

  @Mutation
  private async setNewBookingRole(bookingRole: BookingRoleViewModel) {
    this.newBooking.roles.push(bookingRole);
  }

  @Mutation
  private async setUpdateNewBookingRole(bookingRole: BookingRoleViewModel) {
    const bookingRoleVM = this.newBooking.roles[(parseInt(bookingRole.id))];
    bookingRoleVM.owner = bookingRole.owner
    bookingRoleVM.contactLocation = bookingRole.contactLocation;
    bookingRoleVM.positionDescription = bookingRole.positionDescription;
	bookingRoleVM.chargeRate = bookingRole.chargeRate;
    bookingRoleVM.skills = bookingRole.skills;
    bookingRoleVM.qualifications = bookingRole.qualifications;
    bookingRoleVM.desk = bookingRole.desk;
    bookingRoleVM.uniform = bookingRole.uniform;
    bookingRoleVM.uniformDescription = bookingRole.uniformDescription;
  }
  
  @Mutation
  private async deleteNewBookingRole(bookingRole: BookingRoleViewModel) {
    this.newBooking.roles.splice(this.newBooking.roles.indexOf(bookingRole), 1);
  }

  @Mutation
  private async setCreateBooking(booking: BookingDto) {
    this.newBooking.id = booking.id;
  }
  
}

export const BookingWizardModule = getModule(BookingWizard)
